import { useEffect } from 'react';
import md5 from 'md5';
import { useForethoughtDataV2 } from '@viome/viome-base/src/usecase/Forethought/ForethoughtV2Query';
import { useUserStore } from '@hooks/useUserStore';
import { Sentry } from '@/main';

interface ForethoughtChatProps {
  apiKey: string;
  workflowTag: string;
}

const cleanupForethoughtWidget = () => {
  // Remove the script element
  const scriptElement = document.getElementById('forethought-widget-embed-script');
  if (scriptElement) {
    scriptElement.remove();
  }

  // Remove Forethought widget containers (they use dynamic IDs starting with 'forethought-')
  const widgetContainers = document.querySelectorAll('[id^="forethought-"]');
  widgetContainers.forEach((element) => element.remove());

  // Remove any elements with Forethought-specific classes
  const forethoughtElements = document.querySelectorAll('[class*="forethought"]');
  forethoughtElements.forEach((element) => element.remove());

  // Remove iframe if created by the widget
  const forethoughtIframes = document.querySelectorAll('iframe[src*="forethought"]');
  forethoughtIframes.forEach((iframe) => iframe.remove());
};

export const ForethoughtChat = ({
  apiKey,
  workflowTag,
}: ForethoughtChatProps) => {
  const { user: userData } = useUserStore();
  const userEmail = userData?.email;
  const { data: personalizedData, isLoading } = useForethoughtDataV2();

  useEffect(() => {
    if (isLoading) return;

    cleanupForethoughtWidget();

    const script = document.createElement('script');
    script.src = 'https://solve-widget.forethought.ai/embed.js';
    script.id = 'forethought-widget-embed-script';
    script.setAttribute('data-api-key', apiKey);
    script.setAttribute('data-ft-workflow-tag', workflowTag);
    script.setAttribute('config-ft-size', 'large');

    if (personalizedData) {
      script.setAttribute(
        'data-ft-personalizedAPIJson',
        JSON.stringify(personalizedData),
      );
    }

    if (userEmail) {
      const salt = 'Viome*1$';
      const normalizedEmail: string = userEmail.trim().toLowerCase();
      const finalHash: string = md5(salt + normalizedEmail);

      script.setAttribute(
        'data-ft-Email',
        userEmail,
      );
      script.setAttribute(
        'data-ft-shem',
        finalHash,
      );
    }

    script.onerror = () => {
      Sentry.captureException(
        new Error('ForethoughtChat: Failed to load widget script'),
        {
          extra: { userEmail },
        },
      );
      cleanupForethoughtWidget();
    };

    document.body.appendChild(script);

    // Cleanup function: Remove script and all widget elements on unmount
    return () => {
      cleanupForethoughtWidget();
    };
  }, [apiKey, workflowTag, personalizedData, isLoading, userEmail]);

  return null;
};
